import JSONAPICache from '@ember-data/json-api';
import {
  adapterFor,
  cleanup,
  LegacyNetworkHandler,
  normalize,
  pushPayload,
  serializeRecord,
  serializerFor
} from '@ember-data/legacy-compat';
import { buildSchema, instantiateRecord, modelFor, teardownRecord } from '@ember-data/model/hooks';
import RequestManager from '@ember-data/request';
import Fetch from '@ember-data/request/fetch';
import BaseStore, { CacheHandler } from '@ember-data/store';
import type { ModelStore } from '@ember-data/model/-private';
import param from 'jquery-param';

import type { UploadFile } from 'ember-file-upload';
import type { IResourceName } from 'volta/models/base-model';
import type { UploadOptions } from 'ember-file-upload/interfaces';
import type ApplicationAdapter from 'volta/_pods/application/adapter';
import type { Snapshot } from '@ember-data/legacy-compat/legacy-network-handler/snapshot';
import type { SchemaService } from '@ember-data/store/-types/q/schema-service';
import type { CacheCapabilitiesManager, ModelSchema } from '@ember-data/store/types';
import type { StableRecordIdentifier } from '@warp-drive/core-types';
import type Model from '@ember-data/model';
import type { TypeFromInstance } from '@warp-drive/core-types/record';

function hasRequestManager(store: BaseStore): boolean {
  return 'requestManager' in store;
}

export default class StoreService extends BaseStore {
  // Properties
  // ~~~~~

  serializeRecord = serializeRecord;
  pushPayload = pushPayload;
  adapterFor = adapterFor;
  serializerFor = serializerFor;
  normalize = normalize;
  applicationAdapter = this.adapterFor('application') as unknown as ApplicationAdapter;

  constructor(args: Record<string, unknown>) {
    super(args);
    if (!hasRequestManager(this)) {
      this.requestManager = new RequestManager();
      this.requestManager.use([LegacyNetworkHandler, Fetch]);
    }
    this.requestManager.useCache(CacheHandler);
  }

  override createSchemaService(): SchemaService {
    return buildSchema(this);
  }

  override createCache(capabilities: CacheCapabilitiesManager) {
    return new JSONAPICache(capabilities);
  }

  override instantiateRecord(
    this: ModelStore,
    identifier: StableRecordIdentifier,
    createRecordArgs: Record<string, unknown>
  ): Model {
    return instantiateRecord.call(this, identifier, createRecordArgs);
  }

  override teardownRecord(record: Model) {
    teardownRecord.call(this, record);
  }

  override modelFor<T>(type: TypeFromInstance<T>): ModelSchema<T>;
  override modelFor(type: string): ModelSchema;
  override modelFor(type: string): ModelSchema {
    return (modelFor.call(this, type) as ModelSchema) || super.modelFor(type);
  }

  override destroy() {
    cleanup.call(this);
    super.destroy();
  }

  // Helpers
  // ~~~~~

  rawQuery<OUT = unknown>(modelName: string, args: string[], params?: object): Promise<OUT> {
    const adapter = this.adapterFor(modelName) as unknown as ApplicationAdapter;
    const query = [adapter.pathForType(modelName), ...args].join('/');
    const url = adapter.buildRawURL(query.replace(/\/$/, ''));
    return adapter.ajax(url, 'GET', { data: params }) as Promise<OUT>;
  }

  /**
   * Generates a normal authenticated GET request to a given API end-point
   *
   * @param url API end-point full URL
   * @param params Optional query parameters
   */
  normalGet<T = unknown>(url: string, params?: object) {
    const adapter = this.applicationAdapter;
    const finalUrl = adapter.buildRawURL(url);
    return adapter.ajax(finalUrl, 'GET', { data: params }) as Promise<T>;
  }

  /**
   * Generates a normal authenticated POST request to a given API end-point
   *
   * @param url API end-point full URL
   * @param attrs Optional query parameters
   */
  normalPost<T = unknown>(url: string, attrs: object = {}) {
    const adapter = this.applicationAdapter;
    const finalUrl = adapter.buildRawURL(url);
    const params = { data: attrs };
    return adapter.ajax(finalUrl, 'POST', params) as Promise<T>;
  }

  /**
   * Uploads a file with multipart form authenticated POST request to an API end-point
   *
   * @param url URL of the file uploading API end-point
   * @param file File object
   * @param attrs Optional params
   */
  uploadFile(
    url: string,
    file?: UploadFile,
    attrs?: Partial<Record<string, string | File | Partial<Record<string, string | File>>>>
  ) {
    if (!file) {
      return;
    }
    const data = (attrs?.['data'] ?? attrs ?? {}) as Record<string, string | File>;
    if (attrs?.['data']) {
      delete attrs['data'];
    }
    const adapter = this.applicationAdapter;
    const finalUrl = adapter.buildRawURL(url);
    const headers = adapter.getAuthenticatedHeaders();
    const options = {
      data,
      withCredentials: true,
      headers,
      ...attrs
    } as Partial<UploadOptions>;
    return file.upload(finalUrl, options).then((response: any) => response.json());
  }

  /**
   * GET a file as a `blob`
   *
   * @param url URL of the file to download
   */
  downloadFile(url: string, method?: string, params?: object, body?: object) {
    const adapter = this.applicationAdapter;
    const fetchMethod = method ?? 'GET';
    const finalUrl = `${adapter.buildRawURL(url)}?${params ? param(params) : undefined}`;
    let filename: string | undefined;

    return fetch(finalUrl, {
      method: fetchMethod,
      headers: adapter.headers,
      body: fetchMethod !== 'GET' && body ? JSON.stringify(body) : undefined
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        const header = response.headers.get('Content-Disposition');
        const parts = header?.split(';');
        filename = parts?.[1]?.split('=')[1]?.replaceAll('"', '');

        return response.blob();
      })
      .then((response) => this._onDownloadFileSuccess(response, url, filename));
  }

  normalGetFile(url: string) {
    const adapter = this.applicationAdapter;
    const finalUrl = adapter.buildRawURL(url);
    return fetch(finalUrl, { method: 'GET', headers: adapter.headers })
      .then((response: any) => response.blob())
      .then((response) => URL.createObjectURL(response));
  }

  /**
   * Trigger a collection level non rest query
   *
   * @param modelName The model name
   * @param customQueryName The customQuery name
   * @param recordId The model instance optional id
   * @param params Optional query parameters
   */
  customQuery<OUT = any>(
    modelName: string,
    customQueryName?: string,
    recordId?: string | null,
    params?: object
  ): Promise<OUT> {
    const adapter = this.adapterFor(modelName) as unknown as ApplicationAdapter;
    const url = recordId
      ? adapter.buildURL(modelName, recordId, null as unknown as Snapshot, 'findRecord')
      : adapter.buildURL(modelName, null, null, 'query', {});
    return adapter.ajax(url + `${customQueryName ? '/' + customQueryName : ''}`, 'GET', {
      data: params
    }) as Promise<OUT>;
  }

  nestedQuery(
    modelName: IResourceName,
    modelId: string,
    nestedModelName: IResourceName,
    params?: object
  ): Promise<any> {
    const adapter = this.adapterFor(modelName.plural) as unknown as ApplicationAdapter;
    const url = adapter.buildNestedUrl(modelName.plural, modelId, nestedModelName.plural);
    return adapter.ajax(url, 'GET', { data: params });
  }

  // Private Callback
  // ~~~~~

  /**
   * Creates a link to fool the browser into downloading the file
   * Thus displaying a "save as..." prompt
   *
   * @param data File data
   * @param url URL of the file
   */
  _onDownloadFileSuccess(data: Blob, url: string, filename?: string) {
    const a = document.createElement('a');
    const browserUrl = window.URL.createObjectURL(data);
    a.href = browserUrl;
    a.download = filename ?? url;
    a.click();
    window.URL.revokeObjectURL(browserUrl);
  }
}

declare module '@ember/service' {
  interface Registry {
    store: StoreService;
  }
}
